import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const ShopifyIntegration = () => {
  useSEO({
    title: 'Shopify Integration | Ommni - Sync Products & Inventory',
    description: 'Connect Ommni with Shopify. Sync products, variants, inventory, and orders in real-time. Manage Shopify stores alongside all your commerce channels from one platform.',
    keywords: 'shopify integration, shopify product sync, shopify inventory management, shopify connector',
    canonical: 'https://ommni.io/integrations/shopify',
  });

  const features = [
    "Link products using unique SKUs",
    "Create variants with comprehensive attribute values",
    "Incorporate specific attributes and metadata effectively",
    "Ensure images are uploaded in the correct format and dimensions",
    "Translate product details for multi-language storefronts",
    "Keep pricing, bundled products, and more in sync",
    "Administer multiple Shopify stores simultaneously"
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        <div className="container mx-auto max-w-4xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Shopify Integration
            </h1>
            <p className="text-xl text-muted-foreground">
              Streamline your Shopify product management with Ommni's powerful PIM integration
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-12">
            {/* Section 1 */}
            <Card className="p-8 gradient-card">
              <h2 className="text-3xl font-bold mb-6">
                How does Ommni's PIM Integrate with Shopify?
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Managing product information on Shopify can be an intensive task that consumes significant time and resources for e-commerce businesses. Ommni's integration with Shopify streamlines these processes, allowing your team to allocate more time to other critical business operations.
                </p>
                <p>
                  Our Shopify integration is designed for quick, secure, and straightforward implementation, enabling you to efficiently upload and oversee your Shopify product catalogs from Ommni's PIM system.
                </p>
                <p>
                  With Ommni, you can consolidate the tasks of gathering, organizing, editing, and enriching your product information. Beyond functioning as a PIM for Shopify, our platform ensures seamless synchronization of your product data across all sales channels where you operate.
                </p>
              </div>
            </Card>

            {/* Section 2 */}
            <Card className="p-8 gradient-card">
              <h2 className="text-3xl font-bold mb-6">
                Is Ommni's PIM Connector Compatible with Shopify Plus?
              </h2>
              <p className="text-muted-foreground">
                Indeed, Ommni's software facilitates Product and Media integration for businesses on both the Standard and Shopify Plus plans. This ensures that you can effortlessly manage your product content across various Shopify storefronts.
              </p>
            </Card>

            {/* Section 3 - Features */}
            <Card className="p-8 gradient-card">
              <h2 className="text-3xl font-bold mb-6">
                What functionalities does Ommni's Shopify Integration Offer?
              </h2>
              <div className="space-y-6">
                <p className="text-muted-foreground">
                  Ommni's PIM integration allows for the uploading and automation of product catalogs, ensuring the information is up-to-date, accurate, and free from errors. With an easy setup, our PIM becomes a central hub for managing product narratives, specifications, images, and other digital assets. This centralized system connects to your Shopify store or website, enabling data synchronization seamlessly.
                </p>
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    Optimize your entire product management workflow with Ommni's Shopify connector:
                  </h3>
                  <ul className="space-y-3">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-muted-foreground pt-4">
                  With Ommni's PIM, you gain not only a powerful tool for managing your Shopify product listings but also a strategic partner in expanding your e-commerce footprint.
                </p>
              </div>
            </Card>

            {/* CTA Section */}
            <div className="text-center pt-8">
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">
                  Get Started with Shopify Integration <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default ShopifyIntegration;
